import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { registerSessionExpiredHandler, SessionExpiredOrigin } from './sessionExpiredBridge';
import { logWarning } from 'util/logging';
import { setGcpUserId } from 'util/gcpLogging';
import { useAuth0 } from '@auth0/auth0-react';

interface SessionExpiredContextValue {
  isSessionExpired: boolean;
  reportSessionExpired: (origin: SessionExpiredOrigin) => void;
  clearSessionExpired: () => void;
}

const SessionExpiredContext = createContext<SessionExpiredContextValue>({
  isSessionExpired: false,
  reportSessionExpired: () => {},
  clearSessionExpired: () => {},
});

export function useSessionExpired() {
  return useContext(SessionExpiredContext);
}

// A dead Auth0 session surfaces from several places at once — the scheduled refresh, the initial
// token fetch, and every API call that 401s on the stale header. They all land here, and only the
// first one counts: reporting is idempotent so a burst of 401s can't reopen the dialog or log the
// same broken session more than once.
export function SessionExpiredProvider({ children }: { children: React.ReactNode }) {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const hasReportedRef = useRef(false);
  const { isAuthenticated } = useAuth0();

  const reportSessionExpired = useCallback(
    (origin: SessionExpiredOrigin) => {
      // Only a logged-in user has a session to lose — a stray 401 must not tell an anonymous
      // visitor they were logged out.
      if (!isAuthenticated || hasReportedRef.current) return;
      hasReportedRef.current = true;

      // The honest count of how many real users hit a session that outlived its Auth0 login. A
      // warning, not an error: it is expected and the user can recover from it.
      logWarning('Session expired, prompting re-login', { origin });

      // Stop attributing anything logged from here on to a user whose session is gone.
      setGcpUserId(undefined);

      // A failed initial fetch already shows the full-page SessionExpiredScreen; the dialog on top
      // would only repeat it.
      if (origin !== 'token-configure') setIsSessionExpired(true);
    },
    [isAuthenticated],
  );

  const clearSessionExpired = useCallback(() => {
    hasReportedRef.current = false;
    setIsSessionExpired(false);
  }, []);

  useEffect(() => registerSessionExpiredHandler(reportSessionExpired), [reportSessionExpired]);

  return (
    <SessionExpiredContext.Provider value={{ isSessionExpired, reportSessionExpired, clearSessionExpired }}>
      {children}
    </SessionExpiredContext.Provider>
  );
}
